import { useNavigate } from "react-router-dom"
import { CheckCircle2, ArrowRight, BookOpen, PenTool, Mic2 } from "lucide-react"
import { Button } from "../../../components/ui/button"
import heroImage from "../../../assets/TopHeroImages/3.jpg"

export default function LettresArts() {
    const navigate = useNavigate()
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {heroImage && <img src={heroImage} alt="FLLCA - Lettres, Langues, Communication & Arts" className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105" />}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Faculté des Arts</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Vol. 2024</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Lettres, Langues</span>
                            <span className="block text-white mt-2">Communication & Arts</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                La parole aux créateurs.
                                <span className="italic block mt-1 tracking-tight">Nous croyons que les mots et les images changent le monde.</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button onClick={() => navigate("/admission")} className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Candidater Maintenant
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button onClick={() => document.getElementById("vision")?.scrollIntoView({ behavior: "smooth" })} variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Découvrir les Programmes
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats - Newspaper Style Dateline */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "500+", label: "Presse" },
                        { val: "15K", label: "Ouvrages" },
                        { val: "4", label: "Langues" },
                        { val: "95%", label: "Insertion" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Article - Magazine Layout */}
            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar - Meta Info */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {["Vision", "Programmes", "Atouts"].map((item, i) => (
                                <a key={i} href={`#${item.toLowerCase()}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </a>
                            ))}
                        </nav>

                        <div className="mt-12 pt-8 border-t border-border">
                            <div className="bg-primary/5 p-6 border-l-4 border-primary">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-4 text-primary">En Bref</h4>
                                <dl className="space-y-4 text-[11px]">
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Durée</dt>
                                        <dd className="text-muted-foreground">3 ans</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline">
                                        <dt className="font-bold">Langues</dt>
                                        <dd className="text-muted-foreground">FR/EN/SW/ZH</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </aside>

                    {/* Main Body */}
                    <main className="space-y-16">
                        {/* Section 01 - Vision */}
                        <section id="vision">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Un laboratoire d'idées
                            </h2>

                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    La Faculté des Lettres, Langues, Communication et Arts (FLLCA) est un espace dynamique où la créativité et l'analyse critique se rencontrent. Nous offrons une formation complète combinant excellence académique et innovation.
                                </p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4 mt-8">
                                {[
                                    "Créativité & Innovation",
                                    "Analyse Critique",
                                    "Communication Stratégique",
                                    "Maîtrise Linguistique"
                                ].map((skill, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 bg-secondary/5 border border-border/50 rounded-sm">
                                        <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                                        <span className="text-[11px] font-bold uppercase tracking-tight">{skill}</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Section 02 - Programmes */}
                        <section id="programmes">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Domaines d'Expertise
                            </h2>

                            <div className="grid md:grid-cols-2 gap-6">
                                {[
                                    { title: "Langues & Littérature", tags: ["Anglais", "Français", "Swahili", "Mandarin"] },
                                    { title: "Communication", tags: ["Journalisme", "Relations Publiques", "Médias"] },
                                    { title: "Arts & Culture", tags: ["Médiation Culturelle", "Histoire de l'Art"] }
                                ].map((prog, i) => (
                                    <div key={i} className="interactive-card p-6 border-l-4 border-primary transition-smooth">
                                        <h3 className="heading-4 mb-4">{prog.title}</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {prog.tags.map((tag, j) => (
                                                <span key={j} className="px-2 py-1 bg-secondary/50 text-[10px] font-bold uppercase tracking-wider rounded-sm text-primary">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                    </main>
                </div>
            </article>

            {/* CTA Action - commented out */}
            <footer className="bg-primary text-white border-t-8 border-accent">
                <div className="container py-16 text-center max-w-2xl">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black mb-4 block">Prochaine Étape</span>
                    <h2 className="heading-2 mb-8 leading-tight">
                        Donnez voix à<br />votre créativité.
                    </h2>
                    {/* <Button className="bg-accent text-accent-foreground px-10 py-4 text-base font-black hover:bg-accent/90 transition-smooth group rounded-md shadow-2xl border-b-4 border-black/10">
                        Postuler Maintenant
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button> */}
                </div>
            </footer>
            
        </div>
    )
}
